import { Form, FormikProvider, useFormik } from "formik";
import {
    type ReactNode,
    type Ref,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import type { AgentSkillFlaggedFinding } from "@common/schemas/domains/agentSkills/agent-skill.schema";
import { AgentSkillKind } from "@common/types/agentSkill";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import {
    FLAGGED_FINDING_LABELS,
    FLAGGED_SAVE_MESSAGE,
    SAVE_FAILURE_MESSAGE,
    TOOL_NOT_ATTACHED_HINT,
} from "./agentSkillCopy";
import {
    AgentSkillFormValues,
    agentSkillFormSchemaFor,
    toolNotAttached,
} from "./agentSkillForm";
import {
    getAgentSkillErrorFindings,
    getAgentSkillErrorMessage,
} from "./useAgentSkillLifecycle";

export interface AgentSkillEditorFormHandle {
    /** Starts a fresh editing session from the given values. */
    reset: (values: AgentSkillFormValues) => void;
    /** Loads values on top of the session's baseline, as unsaved changes. */
    setValues: (values: AgentSkillFormValues) => void;
}

interface Props {
    submitLabel: string;
    initialValues: AgentSkillFormValues;
    kind?: AgentSkillKind;
    initialWarnings?: string[];
    buttonSize?: "md" | "lg";
    className?: string;
    formRef?: Ref<AgentSkillEditorFormHandle>;
    /** The fields, laid out by the consumer. */
    children: ReactNode;
    /** Places the Cancel and Save buttons; defaults to a right-aligned row. */
    renderActions?: (actions: ReactNode) => ReactNode;
    /** Resolves to the validator's warnings, or nothing when no save happened. */
    onSubmit: (values: AgentSkillFormValues) => Promise<string[] | void>;
    onClose: () => void;
    /**
     * After a save with no warnings; the saved values become the baseline.
     * Without it the form closes as on Cancel.
     */
    onSaved?: () => void;
    /** Follows Formik's `dirty`: the values differ from the session's start. */
    onDirtyChange?: (isDirty: boolean) => void;
}

const defaultRenderActions = (actions: ReactNode) => (
    <HStack gap={12} justify="end">
        {actions}
    </HStack>
);

export const AgentSkillEditorForm = ({
    submitLabel,
    initialValues,
    kind = AgentSkillKind.FIRM,
    initialWarnings = [],
    buttonSize = "lg",
    className,
    formRef,
    children,
    renderActions = defaultRenderActions,
    onSubmit,
    onClose,
    onSaved,
    onDirtyChange,
}: Props) => {
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [findings, setFindings] = useState<AgentSkillFlaggedFinding[]>([]);
    const [warnings, setWarnings] = useState<string[]>(initialWarnings);
    const formik = useFormik<AgentSkillFormValues>({
        initialValues,
        validate: validateFormikWithZodSchema(agentSkillFormSchemaFor(kind)),
        onSubmit: submitHelper({
            loading: false,
            handler: async (values) => {
                setSubmitError(null);
                setFindings([]);
                const nextWarnings = await onSubmit(values);
                // The page keeps editing: the saved values become the
                // baseline whether or not the validator warned.
                if (onSaved) {
                    formik.resetForm({ values });
                    setWarnings(nextWarnings ?? []);
                    onSaved();
                    return;
                }
                if (nextWarnings && nextWarnings.length > 0) {
                    setWarnings(nextWarnings);
                    return;
                }
                handleClose();
            },
            failureHandler: async (error) => {
                setFindings(getAgentSkillErrorFindings(error));
                setSubmitError(
                    getAgentSkillErrorMessage(error, SAVE_FAILURE_MESSAGE),
                );
            },
        }),
    });
    useEffect(() => {
        onDirtyChange?.(formik.dirty);
    }, [formik.dirty, onDirtyChange]);
    useImperativeHandle(
        formRef,
        () => ({
            reset: (values) => {
                formik.resetForm({ values });
                setSubmitError(null);
                setFindings([]);
                setWarnings([]);
            },
            setValues: (values) => {
                void formik.setValues(values);
            },
        }),
        [formik],
    );

    const handleClose = () => {
        formik.resetForm();
        setSubmitError(null);
        setFindings([]);
        setWarnings([]);
        onClose();
    };

    const unattachedTools = warnings.flatMap((warning) => {
        const tool = toolNotAttached(warning);
        return tool === null ? [] : [tool];
    });

    return (
        <FormikProvider value={formik}>
            <Form className={className}>
                {children}
                {(findings.length > 0 ||
                    unattachedTools.length > 0 ||
                    submitError) && (
                    <VStack gap={8}>
                        {findings.length > 0 && (
                            <VStack gap={4} className="text-danger">
                                <strong>{FLAGGED_SAVE_MESSAGE}</strong>
                                {findings.map((finding, index) => (
                                    <span key={`${finding.code}-${index}`}>
                                        {FLAGGED_FINDING_LABELS[finding.code] ??
                                            finding.code}
                                        {finding.line !== undefined
                                            ? ` (line ${finding.line})`
                                            : ""}
                                        : “{finding.excerpt}”
                                    </span>
                                ))}
                            </VStack>
                        )}
                        {unattachedTools.length > 0 && (
                            <span className="text-secondary">
                                {TOOL_NOT_ATTACHED_HINT}:{" "}
                                {unattachedTools.join(", ")}
                            </span>
                        )}
                        {submitError && findings.length === 0 && (
                            <p className="text-danger">{submitError}</p>
                        )}
                    </VStack>
                )}
                {renderActions(
                    <>
                        <Button
                            type="button"
                            variant="secondary"
                            size={buttonSize}
                            onClick={handleClose}
                            disabled={formik.isSubmitting}
                        >
                            {warnings.length > 0 ? "Close" : "Cancel"}
                        </Button>
                        <ButtonWithLoader
                            type="submit"
                            variant="primary"
                            size={buttonSize}
                            loading={formik.isSubmitting}
                            hotKey="mod+enter"
                            hotKeyDisplayPosition="inline"
                            hotKeyOptions={{
                                description: submitLabel,
                                preventDefault: true,
                                enableOnFormTags: true,
                                enableOnContentEditable: true,
                            }}
                        >
                            {submitLabel}
                        </ButtonWithLoader>
                    </>,
                )}
            </Form>
        </FormikProvider>
    );
};
